import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getSurgeonFactor } from "@/lib/api";
import { log } from "@/lib/log";

const EMPTY_SF = { count: 0, mean_error: 0, suggested_a_constant_delta: 0, by_lens: [], min_followups: 3 };

export function useSurgeonFactor(lensId, setPatient) {
  const [sf, setSf] = useState(EMPTY_SF);

  const refresh = useCallback(async () => {
    try {
      setSf(await getSurgeonFactor());
    } catch (err) {
      log.error("Failed to load surgeon factor", err);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const currentLensSF = useMemo(
    () => (sf.by_lens || []).find((l) => l.lens_id === lensId) || null,
    [sf, lensId]
  );

  const applyNudge = (delta, label) => {
    setPatient((p) => ({ ...p, a_constant: Math.round(((parseFloat(p.a_constant) || 119.84) + delta) * 100) / 100 }));
    toast.success(`${label}: A-constant nudged by ${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`);
  };

  const applyGlobal = () => applyNudge(sf.suggested_a_constant_delta || 0, "Global");
  const applyLens = () => currentLensSF && applyNudge(currentLensSF.suggested_a_constant_delta || 0, currentLensSF.lens_name);
  const applyLensC = () => {
    if (!currentLensSF?.learned_c_constant) return;
    setPatient((p) => ({ ...p, c_constant: currentLensSF.learned_c_constant }));
    toast.success(`${currentLensSF.lens_name}: C-constant set to ${currentLensSF.learned_c_constant.toFixed(3)}`);
  };

  return { sf, currentLensSF, refresh, applyGlobal, applyLens, applyLensC };
}
